import { useEffect, useRef, useState } from 'react';

// The kitchen film: a 45 s animation of how the macarons are made (promo/scene.html, rendered by promo/render.py;
// code-made illustration, no footage, no people). Nothing downloads until the section is near the screen
// (preload="none"); it then plays muted and looped while visible and pauses when it scrolls away. With reduced motion
// it never starts by itself: the poster shows and the play button starts it. The step texts are drawn into the film
// and repeated in the step list below; the caption track carries them too.
export const PROMO = {
  base: `${process.env.PUBLIC_URL || ''}/promo`,
  steps: [
    'Ingredients: almond flour, icing sugar, egg whites and sugar.',
    'Macaronage: whip a glossy meringue, then fold until the batter flows like lava.',
    'Piping: pipe even rounds, then tap the tray to release the air.',
    'Resting: rest until a dry skin forms on every shell.',
    'Baking: bake low and slow, until the shells rise on their feet.',
    'Filling: fill with ganache, buttercream or fruit curd, then sandwich.',
    'Boxing: boxed by hand, the morning you collect.',
  ],
};

const REDUCE = '(prefers-reduced-motion: reduce)';

export function prefersReducedMotion() {
  return typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(REDUCE).matches;
}

export function PromoVideo() {
  const video = useRef(null);
  const [playing, setPlaying] = useState(false);
  const userPaused = useRef(false);

  useEffect(() => {
    const el = video.current;
    if (!el) return undefined;
    // Off screen it always pauses (also after a manual start); on screen it starts by itself only while reduced motion
    // is off and the visitor has not paused it. Turning reduced motion on pauses it at once (Codex PROMO-01).
    const io = typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) el.pause();
      else if (!userPaused.current && !prefersReducedMotion()) el.play().catch(() => {}); // refused: poster + button
    }, { threshold: 0.35 });
    if (io) io.observe(el);
    const mq = window.matchMedia ? window.matchMedia(REDUCE) : null;
    const onMotion = () => { if (mq.matches) el.pause(); };
    if (mq && mq.addEventListener) mq.addEventListener('change', onMotion);
    else if (mq) mq.addListener(onMotion); // Safari < 14
    return () => {
      if (io) io.disconnect();
      if (mq && mq.removeEventListener) mq.removeEventListener('change', onMotion);
      else if (mq) mq.removeListener(onMotion);
    };
  }, []);

  const toggle = () => {
    const el = video.current;
    if (!el) return;
    if (el.paused) {
      userPaused.current = false;
      el.play().catch(() => {});
    } else {
      userPaused.current = true;
      el.pause();
    }
  };

  return (
    <section className="promo reveal" aria-labelledby="promo-title">
      <div className="section-head">
        <p className="kicker">From our kitchen</p>
        <h2 className="display" id="promo-title">How a macaron is <em className="script">made</em></h2>
        <p>Seven steps, every morning, before the doors open.</p>
      </div>
      <div className="promo-frame">
        <video
          ref={video}
          className="promo-video"
          poster={`${PROMO.base}/poster.jpg`}
          preload="none"
          muted
          loop
          playsInline
          width="1280"
          height="720"
          aria-describedby="promo-steps"
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
        >
          <source src={`${PROMO.base}/macaron-promo.webm`} type="video/webm" />
          <source src={`${PROMO.base}/macaron-promo.mp4`} type="video/mp4" />
          <track kind="captions" src={`${PROMO.base}/captions.vtt`} srcLang="en" label="English" />
        </video>
        <button type="button" className="promo-toggle" onClick={toggle} aria-label={playing ? 'Pause the kitchen film' : 'Play the kitchen film'}>
          {playing ? (
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 5h3v14H7zM14 5h3v14h-3z" /></svg>
          ) : (
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5l11 7-11 7z" /></svg>
          )}
        </button>
      </div>
      <p className="promo-note">Animated with code, made with Claude: an illustration of our method, not footage. No sound.</p>
      <details className="promo-steps">
        <summary>Read the steps</summary>
        <ol id="promo-steps">
          {PROMO.steps.map((s) => <li key={s}>{s}</li>)}
        </ol>
      </details>
    </section>
  );
}
